import { Injectable } from '@angular/core';
import { BillingInterval, stripeConfig } from '../../environments/stripe';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class StripeService {
  constructor(private auth: AuthService) {}

  buyNow(productName: string, interval?: BillingInterval, termsDocId?: string): void {
    const entry = stripeConfig.paymentLinks[productName];
    const url = typeof entry === 'string' ? entry : entry?.[interval ?? 'yearly'];
    if (!url || url.includes('REPLACE_')) {
      console.warn(`No Stripe Payment Link configured for: ${productName}${interval ? ` (${interval})` : ''}`);
      return;
    }

    // Signed-in users pass their UID so the webhook can grant access immediately.
    // Guest users pass the terms acceptance doc ID (prefixed "terms:") so the
    // webhook can backfill the email + name onto their terms record once checkout
    // completes. The grant itself falls back to email matching in both cases.
    const uid = this.auth.currentUser?.uid;
    // Encode both uid (for tool grant) and pendingId (to finalize terms record).
    // Guest: t--{pendingId}   Signed-in: {uid}--{pendingId}
    const clientRefId = uid
      ? (termsDocId ? `${uid}--${termsDocId}` : uid)
      : (termsDocId ? `t--${termsDocId}` : undefined);
    const finalUrl = clientRefId ? `${url}?client_reference_id=${clientRefId}` : url;
    window.open(finalUrl, '_blank');
  }

  // Redirects the signed-in caller into their Stripe Billing Portal session,
  // where they can update payment method, view invoices, or cancel.
  async manageSubscription(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Sign in to manage your subscription.');

    const idToken = await user.getIdToken();
    const res = await fetch('/api/create-portal-session', {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ returnUrl: window.location.href }),
    });
    let data: { url?: string; error?: string };
    try {
      data = await res.json();
    } catch (err) {
      // /api/* isn't served under `ng serve` — this needs `netlify dev` (or a real
      // deploy) locally, otherwise the fetch falls through to the SPA's index.html.
      console.error('manageSubscription: response was not JSON (is /api/* being served?)', err);
      throw new Error('Could not open billing portal. Please try again.');
    }
    if (!res.ok || !data.url) {
      throw new Error(data.error || 'Could not open billing portal.');
    }
    window.location.href = data.url;
  }
}
